import { NetworkCommunity } from "@voxmedia/duet-context/context/community-context";

import * as cycloneCommunities from "./cyclone/cyclone-communities.sbnation";

export * from "./cyclone/cyclone-communities.sbnation";

export const SB_NATION_COMMUNITY_ID = 247;

/**
 * Feed-query inputs derived from a cyclone community selection.
 *
 * Mobile feed requests currently arrive with only a cyclone community ID. This
 * lookup allows the Honeypot feed endpoint to expand that into the paired
 * Chorus community ID with any optional category filter required for the
 * WordPress branch of the combined feed query.
 */
export interface CycloneCommunityFeedQueryConfig {
  chorusCommunityId: number;
  feedCategoryFilter?: string[];
  feedSuperCategoryFilter?: string[];
}

type CycloneCommunityWithFeedQueryConfig = NonNullable<
  NetworkCommunity["cycloneCommunities"]
>[string] & {
  feedCategoryFilter?: string[];
  feedSuperCategoryFilter?: string[];
};

type NetworkCommunityWithFeedQueryConfig = Omit<
  NetworkCommunity,
  "cycloneCommunities"
> & {
  cycloneCommunities?: Record<string, CycloneCommunityWithFeedQueryConfig>;
};

export const SB_NATION_COMMUNITIES: Record<
  number,
  NetworkCommunityWithFeedQueryConfig
> = {
  [SB_NATION_COMMUNITY_ID]: {
    name: "SB Nation",
    displayName: "SB Nation",
    subdomain: null,
    domain: "sbnation.com",
    wpSiteId: 2,
    isPrimaryCommunity: true,
    cycloneCommunities: {
      [cycloneCommunities.SBNATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "SB Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.SBNATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 4,
      },
      [cycloneCommunities.SB_NATION_PRODUCT_CYCLONE_COMMUNITY_ID]: {
        displayName: "SBNation Product",
        feedCategoryFilter: ["product"],
        cycloneCommunityId:
          cycloneCommunities.SB_NATION_PRODUCT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 5,
        cycloneCommunityPrefix: "/product",
      },
      [cycloneCommunities.MARCHMADNESS_CYCLONE_COMMUNITY_ID]: {
        displayName: "March Madness",
        feedCategoryFilter: ["march-madness"],
        cycloneCommunityId:
          cycloneCommunities.MARCHMADNESS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 191,
        cycloneCommunityPrefix: "/march-madness",
      },
      [cycloneCommunities.NFL_CYCLONE_COMMUNITY_ID]: {
        displayName: "NFL",
        feedCategoryFilter: ["nfl"],
        cycloneCommunityId: cycloneCommunities.NFL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 192,
        cycloneCommunityPrefix: "/nfl",
      },
      [cycloneCommunities.NBA_CYCLONE_COMMUNITY_ID]: {
        displayName: "NBA",
        feedCategoryFilter: ["nba"],
        cycloneCommunityId: cycloneCommunities.NBA_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 193,
        cycloneCommunityPrefix: "/nba",
      },
      [cycloneCommunities.MLB_CYCLONE_COMMUNITY_ID]: {
        displayName: "MLB",
        feedCategoryFilter: ["mlb"],
        cycloneCommunityId: cycloneCommunities.MLB_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 194,
        cycloneCommunityPrefix: "/mlb",
      },
      [cycloneCommunities.NHL_CYCLONE_COMMUNITY_ID]: {
        displayName: "NHL",
        feedCategoryFilter: ["nhl"],
        cycloneCommunityId: cycloneCommunities.NHL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 195,
        cycloneCommunityPrefix: "/nhl",
      },
      [cycloneCommunities.SOCCER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Soccer",
        feedCategoryFilter: ["soccer"],
        cycloneCommunityId: cycloneCommunities.SOCCER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 196,
        cycloneCommunityPrefix: "/soccer",
      },
      [cycloneCommunities.COLLEGE_FOOTBALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "College Football",
        feedCategoryFilter: ["college-football"],
        cycloneCommunityId:
          cycloneCommunities.COLLEGE_FOOTBALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 197,
        cycloneCommunityPrefix: "/college-football",
      },
      [cycloneCommunities.COLLEGE_BASKETBALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "College Basketball",
        feedCategoryFilter: ["college-basketball"],
        cycloneCommunityId:
          cycloneCommunities.COLLEGE_BASKETBALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 198,
        cycloneCommunityPrefix: "/college-basketball",
      },
      [cycloneCommunities.FORMULA_ONE_CYCLONE_COMMUNITY_ID]: {
        displayName: "F1",
        feedCategoryFilter: ["formula-one"],
        cycloneCommunityId: cycloneCommunities.FORMULA_ONE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 199,
        cycloneCommunityPrefix: "/formula-one",
      },
      [cycloneCommunities.NFC_EAST_MIXTAPE_CYCLONE_COMMUNITY_ID]: {
        displayName: "NFC East Mixtape",
        feedCategoryFilter: ["nfc-east-mixtape"],
        cycloneCommunityId:
          cycloneCommunities.NFC_EAST_MIXTAPE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 201,
        cycloneCommunityPrefix: "/nfc-east-mixtape",
      },
      [cycloneCommunities.FISH_STRIPES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Fish Stripes",
        cycloneCommunityId:
          cycloneCommunities.FISH_STRIPES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 202,
        cycloneCommunityPrefix: "/fish-stripes",
      },
      [cycloneCommunities.HALOS_HEAVEN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Halos Heaven",
        cycloneCommunityId:
          cycloneCommunities.HALOS_HEAVEN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 203,
        cycloneCommunityPrefix: "/halos-heaven",
      },
      [cycloneCommunities.SABRES_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Sabres Community",
        cycloneCommunityId:
          cycloneCommunities.SABRES_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 204,
        cycloneCommunityPrefix: "/buffalo-sabres",
      },
      [cycloneCommunities.RED_WINGS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Red Wings Community",
        cycloneCommunityId:
          cycloneCommunities.RED_WINGS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 205,
        cycloneCommunityPrefix: "/detroit-red-wings",
      },
      [cycloneCommunities.FLORIDA_PANTHERS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Florida Panthers Community",
        cycloneCommunityId:
          cycloneCommunities.FLORIDA_PANTHERS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 206,
        cycloneCommunityPrefix: "/florida-panthers",
      },
      [cycloneCommunities.CANADIENS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Canadiens Community",
        cycloneCommunityId:
          cycloneCommunities.CANADIENS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 207,
        cycloneCommunityPrefix: "/montreal-canadiens",
      },
      [cycloneCommunities.SENATORS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Senators Community",
        cycloneCommunityId:
          cycloneCommunities.SENATORS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 208,
        cycloneCommunityPrefix: "/ottawa-senators",
      },
      [cycloneCommunities.LIGHTNING_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Lightning Community",
        cycloneCommunityId:
          cycloneCommunities.LIGHTNING_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 209,
        cycloneCommunityPrefix: "/tb-lightning",
      },
      [cycloneCommunities.MAPLE_LEAFS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Maple Leafs Community",
        cycloneCommunityId:
          cycloneCommunities.MAPLE_LEAFS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 210,
        cycloneCommunityPrefix: "/toronto-maple-leafs",
      },
      [cycloneCommunities.BLUE_JACKETS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blue Jackets Community",
        cycloneCommunityId:
          cycloneCommunities.BLUE_JACKETS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 211,
        cycloneCommunityPrefix: "/columbus-blue-jackets",
      },
      [cycloneCommunities.NY_RANGERS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "NY Rangers Community",
        cycloneCommunityId:
          cycloneCommunities.NY_RANGERS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 212,
        cycloneCommunityPrefix: "/ny-rangers",
      },
      [cycloneCommunities.FLYERS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Flyers Community",
        cycloneCommunityId:
          cycloneCommunities.FLYERS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 213,
        cycloneCommunityPrefix: "/philadelphia-flyers",
      },
      [cycloneCommunities.CAPITALS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Capitals Community",
        cycloneCommunityId:
          cycloneCommunities.CAPITALS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 214,
        cycloneCommunityPrefix: "/washington-capitals",
      },
      [cycloneCommunities.BLACKHAWKS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blackhawks Community",
        cycloneCommunityId:
          cycloneCommunities.BLACKHAWKS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 215,
        cycloneCommunityPrefix: "/chicago-blackhawks",
      },
      [cycloneCommunities.STARS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Stars Community",
        cycloneCommunityId:
          cycloneCommunities.STARS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 216,
        cycloneCommunityPrefix: "/dallas-stars",
      },
      [cycloneCommunities.WILD_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Wild Community",
        cycloneCommunityId:
          cycloneCommunities.WILD_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 217,
        cycloneCommunityPrefix: "/minnesota-wild",
      },
      [cycloneCommunities.PREDATORS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Predators Community",
        cycloneCommunityId:
          cycloneCommunities.PREDATORS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 218,
        cycloneCommunityPrefix: "/nashville-predators",
      },
      [cycloneCommunities.BLUES_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blues Community",
        cycloneCommunityId:
          cycloneCommunities.BLUES_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 219,
        cycloneCommunityPrefix: "/st-louis-blues",
      },
      [cycloneCommunities.MAMMOTH_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Mammoth Community",
        cycloneCommunityId:
          cycloneCommunities.MAMMOTH_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 220,
        cycloneCommunityPrefix: "/utah-mammoth",
      },
      [cycloneCommunities.WINNIPEG_JETS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Winnipeg Jets Community",
        cycloneCommunityId:
          cycloneCommunities.WINNIPEG_JETS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 221,
        cycloneCommunityPrefix: "/winnipeg-jets",
      },
      [cycloneCommunities.ANAHEIM_DUCKS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Anaheim Ducks Community",
        cycloneCommunityId:
          cycloneCommunities.ANAHEIM_DUCKS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 222,
        cycloneCommunityPrefix: "/anaheim-ducks",
      },
      [cycloneCommunities.FLAMES_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Flames Community",
        cycloneCommunityId:
          cycloneCommunities.FLAMES_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 223,
        cycloneCommunityPrefix: "/calgary-flames",
      },
      [cycloneCommunities.COPPER_AND_BLUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Copper and Blue",
        cycloneCommunityId:
          cycloneCommunities.COPPER_AND_BLUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 224,
        cycloneCommunityPrefix: "/edmonton-oilers",
      },
      [cycloneCommunities.LA_KINGS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "LA Kings Community",
        cycloneCommunityId:
          cycloneCommunities.LA_KINGS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 225,
        cycloneCommunityPrefix: "/la-kings",
      },
      [cycloneCommunities.SHARKS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Sharks Community",
        cycloneCommunityId:
          cycloneCommunities.SHARKS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 226,
        cycloneCommunityPrefix: "/san-jose-sharks",
      },
      [cycloneCommunities.KRAKEN_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Kraken Community",
        cycloneCommunityId:
          cycloneCommunities.KRAKEN_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 227,
        cycloneCommunityPrefix: "/seattle-kraken",
      },
      [cycloneCommunities.CANUCKS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Canucks Community",
        cycloneCommunityId:
          cycloneCommunities.CANUCKS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 228,
        cycloneCommunityPrefix: "/vancouver-canucks",
      },
      [cycloneCommunities.GOLDEN_KNIGHTS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Golden Knights Community",
        cycloneCommunityId:
          cycloneCommunities.GOLDEN_KNIGHTS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 229,
        cycloneCommunityPrefix: "/vegas-golden-knights",
      },
      [cycloneCommunities.HORNETS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Hornets Community",
        cycloneCommunityId:
          cycloneCommunities.HORNETS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 230,
        cycloneCommunityPrefix: "/charlotte-hornets",
      },
      [cycloneCommunities.BULLS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bulls Community",
        cycloneCommunityId:
          cycloneCommunities.BULLS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 231,
        cycloneCommunityPrefix: "/chicago-bulls",
      },
      [cycloneCommunities.NUGGETS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Nuggets Community",
        cycloneCommunityId:
          cycloneCommunities.NUGGETS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 232,
        cycloneCommunityPrefix: "/denver-nuggets",
      },
      [cycloneCommunities.PACERS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Pacers Community",
        cycloneCommunityId:
          cycloneCommunities.PACERS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 233,
        cycloneCommunityPrefix: "/indiana-pacers",
      },
      [cycloneCommunities.GRIZZLIES_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Grizzlies Community",
        cycloneCommunityId:
          cycloneCommunities.GRIZZLIES_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 234,
        cycloneCommunityPrefix: "/memphis-grizzlies",
      },
      [cycloneCommunities.HEAT_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Heat Community",
        cycloneCommunityId:
          cycloneCommunities.HEAT_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 235,
        cycloneCommunityPrefix: "/miami-heat",
      },
      [cycloneCommunities.PELICANS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Pelicans Community",
        cycloneCommunityId:
          cycloneCommunities.PELICANS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 236,
        cycloneCommunityPrefix: "/new-orleans-pelicans",
      },
      [cycloneCommunities.THUNDER_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Thunder Community",
        cycloneCommunityId:
          cycloneCommunities.THUNDER_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 237,
        cycloneCommunityPrefix: "/okc-thunder",
      },
      [cycloneCommunities.ORLANDO_PINSTRIPED_POST_CYCLONE_COMMUNITY_ID]: {
        displayName: "Orlando Pinstriped Post",
        cycloneCommunityId:
          cycloneCommunities.ORLANDO_PINSTRIPED_POST_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 238,
        cycloneCommunityPrefix: "/orlando-pinstriped-post",
      },
      [cycloneCommunities.SACRAMENTO_KINGS_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Sacramento Kings Community",
        cycloneCommunityId:
          cycloneCommunities.SACRAMENTO_KINGS_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 239,
        cycloneCommunityPrefix: "/sacramento-kings",
      },
      [cycloneCommunities.PSG_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "PSG Community",
        cycloneCommunityId:
          cycloneCommunities.PSG_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 240,
        cycloneCommunityPrefix: "/psg",
      },
      [cycloneCommunities.GARNET_AND_BLACK_ATTACK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Garnet & Black Attack",
        cycloneCommunityId:
          cycloneCommunities.GARNET_AND_BLACK_ATTACK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 241,
        cycloneCommunityPrefix: "/garnet-black-attack",
      },
      [cycloneCommunities.COLLEGE_AND_MAGNOLIA_CYCLONE_COMMUNITY_ID]: {
        displayName: "College and Magnolia",
        cycloneCommunityId:
          cycloneCommunities.COLLEGE_AND_MAGNOLIA_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 242,
        cycloneCommunityPrefix: "/college-and-magnolia",
      },
      [cycloneCommunities.ARKANSAS_FIGHT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Arkansas Fight",
        cycloneCommunityId:
          cycloneCommunities.ARKANSAS_FIGHT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 243,
        cycloneCommunityPrefix: "/arkansas-fight",
      },
      [cycloneCommunities.FOR_WHOM_THE_COWBELL_TOLLS_CYCLONE_COMMUNITY_ID]: {
        displayName: "For Whom the Cowbell Tolls",
        cycloneCommunityId:
          cycloneCommunities.FOR_WHOM_THE_COWBELL_TOLLS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 244,
        cycloneCommunityPrefix: "/for-whom-the-cowbell-tolls",
      },
      [cycloneCommunities.CRIMSON_AND_CREAM_MACHINE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Crimson & Cream Machine",
        cycloneCommunityId:
          cycloneCommunities.CRIMSON_AND_CREAM_MACHINE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 245,
        cycloneCommunityPrefix: "/crimson-cream-machine",
      },
      [cycloneCommunities.CONQUEST_CHRONICLES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Conquest Chronicles",
        cycloneCommunityId:
          cycloneCommunities.CONQUEST_CHRONICLES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 246,
        cycloneCommunityPrefix: "/conquest-chronicles",
      },
      [cycloneCommunities.VIVA_THE_MATADORS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Viva The Matadors",
        cycloneCommunityId:
          cycloneCommunities.VIVA_THE_MATADORS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 247,
        cycloneCommunityPrefix: "/viva-the-matadors",
      },
      [cycloneCommunities.BLOCK_U_CYCLONE_COMMUNITY_ID]: {
        displayName: "Block U",
        cycloneCommunityId: cycloneCommunities.BLOCK_U_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 248,
        cycloneCommunityPrefix: "/block-u",
      },
      [cycloneCommunities.OUR_DAILY_BEARS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Our Daily Bears",
        cycloneCommunityId:
          cycloneCommunities.OUR_DAILY_BEARS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 249,
        cycloneCommunityPrefix: "/our-daily-bears",
      },
      [cycloneCommunities.COWBOYS_RIDE_FOR_FREE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Cowboys Ride For Free",
        cycloneCommunityId:
          cycloneCommunities.COWBOYS_RIDE_FOR_FREE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 250,
        cycloneCommunityPrefix: "/cowboys-ride-for-free",
      },
      [cycloneCommunities.CARDIAC_HILL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Cardiac Hill",
        cycloneCommunityId:
          cycloneCommunities.CARDIAC_HILL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 251,
        cycloneCommunityPrefix: "/cardiac-hill",
      },
      [cycloneCommunities.CALIFORNIA_GOLDEN_BLOGS_CYCLONE_COMMUNITY_ID]: {
        displayName: "California Golden Blogs",
        cycloneCommunityId:
          cycloneCommunities.CALIFORNIA_GOLDEN_BLOGS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 252,
        cycloneCommunityPrefix: "/california-golden-blogs",
      },
      [cycloneCommunities.RULE_OF_TREE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Rule of Tree",
        cycloneCommunityId:
          cycloneCommunities.RULE_OF_TREE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 253,
        cycloneCommunityPrefix: "/rule-of-tree",
      },
      [cycloneCommunities.SMU_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "SMU Community",
        cycloneCommunityId:
          cycloneCommunities.SMU_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 254,
        cycloneCommunityPrefix: "/smu",
      },
      [cycloneCommunities.SEATTLE_NBA_EXPANSION_COMMUNITY_CYCLONE_COMMUNITY_ID]:
        {
          displayName: "Seattle NBA Expansion Community",
          cycloneCommunityId:
            cycloneCommunities.SEATTLE_NBA_EXPANSION_COMMUNITY_CYCLONE_COMMUNITY_ID,
          cycloneCommunityCacheId: 255,
          cycloneCommunityPrefix: "/seattle-nba",
        },
      [cycloneCommunities.LV_NBA_EXPANSION_COMMUNITY_CYCLONE_COMMUNITY_ID]: {
        displayName: "LV NBA Expansion Community",
        cycloneCommunityId:
          cycloneCommunities.LV_NBA_EXPANSION_COMMUNITY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 256,
        cycloneCommunityPrefix: "/vegas-nba",
      },
      [cycloneCommunities.BREAKAWAY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Breakaway",
        feedSuperCategoryFilter: ["womens-sports"],
        cycloneCommunityId: cycloneCommunities.BREAKAWAY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 10,
        cycloneCommunityPrefix: "/womens-sports",
      },
      [cycloneCommunities.PLAYING_THROUGH_CYCLONE_COMMUNITY_ID]: {
        displayName: "Playing Through",
        feedCategoryFilter: ["golf"],
        cycloneCommunityId:
          cycloneCommunities.PLAYING_THROUGH_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 11,
        cycloneCommunityPrefix: "/golf",
      },
    },
  },
  77: {
    name: "Acme Packing Company",
    displayName: "Acme Packing Company",
    subdomain: null,
    domain: "acmepackingcompany.com",
    wpSiteId: 3,
    cycloneCommunities: {
      [cycloneCommunities.ACME_PACKING_COMPANY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Acme Packing Company",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ACME_PACKING_COMPANY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 79,
      },
    },
  },
  378: {
    name: "The AC Milan Offside",
    displayName: "The AC Milan Offside",
    subdomain: "acmilan",
    domain: "acmilan.theoffside.com",
    wpSiteId: 4,
    cycloneCommunities: {
      [cycloneCommunities.THE_AC_MILAN_OFFSIDE_CYCLONE_COMMUNITY_ID]: {
        displayName: "The AC Milan Offside",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_AC_MILAN_OFFSIDE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 172,
      },
    },
  },
  145: {
    name: "Addicted To Quack",
    displayName: "Addicted To Quack",
    subdomain: null,
    domain: "addictedtoquack.com",
    wpSiteId: 5,
    cycloneCommunities: {
      [cycloneCommunities.ADDICTED_TO_QUACK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Addicted To Quack",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ADDICTED_TO_QUACK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 102,
      },
    },
  },
  176: {
    name: "All About The Jersey",
    displayName: "All About The Jersey",
    subdomain: null,
    domain: "allaboutthejersey.com",
    wpSiteId: 6,
    cycloneCommunities: {
      [cycloneCommunities.ALL_ABOUT_THE_JERSEY_CYCLONE_COMMUNITY_ID]: {
        displayName: "All About The Jersey",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ALL_ABOUT_THE_JERSEY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 118,
      },
    },
  },
  756: {
    name: "All For XI",
    displayName: "All For XI",
    subdomain: null,
    domain: "allforxi.com",
    wpSiteId: 7,
    cycloneCommunities: {
      [cycloneCommunities.ALL_FOR_XI_CYCLONE_COMMUNITY_ID]: {
        displayName: "All For XI",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.ALL_FOR_XI_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 186,
      },
    },
  },
  35: {
    name: "Amazin' Avenue",
    displayName: "Amazin' Avenue",
    subdomain: null,
    domain: "amazinavenue.com",
    wpSiteId: 8,
    cycloneCommunities: {
      [cycloneCommunities.AMAZIN_AVENUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Amazin' Avenue",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.AMAZIN_AVENUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 52,
      },
    },
  },
  259: {
    name: "Anchor Of Gold",
    displayName: "Anchor Of Gold",
    subdomain: null,
    domain: "anchorofgold.com",
    wpSiteId: 9,
    cycloneCommunities: {
      [cycloneCommunities.ANCHOR_OF_GOLD_CYCLONE_COMMUNITY_ID]: {
        displayName: "Anchor Of Gold",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ANCHOR_OF_GOLD_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 147,
      },
    },
  },
  101: {
    name: "And The Valley Shook",
    displayName: "And The Valley Shook",
    subdomain: null,
    domain: "andthevalleyshook.com",
    wpSiteId: 10,
    cycloneCommunities: {
      [cycloneCommunities.AND_THE_VALLEY_SHOOK_CYCLONE_COMMUNITY_ID]: {
        displayName: "And The Valley Shook",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.AND_THE_VALLEY_SHOOK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 87,
      },
    },
  },
  279: {
    name: "Anonymous Eagle",
    displayName: "Anonymous Eagle",
    subdomain: null,
    domain: "anonymouseagle.com",
    wpSiteId: 11,
    cycloneCommunities: {
      [cycloneCommunities.ANONYMOUS_EAGLE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Anonymous Eagle",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ANONYMOUS_EAGLE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 152,
      },
    },
  },
  59: {
    name: "Arrowhead Pride",
    displayName: "Arrowhead Pride",
    subdomain: null,
    domain: "arrowheadpride.com",
    wpSiteId: 12,
    cycloneCommunities: {
      [cycloneCommunities.ARROWHEAD_PRIDE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Arrowhead Pride",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ARROWHEAD_PRIDE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 18,
      },
    },
  },
  109: {
    name: "A Sea Of Blue",
    displayName: "A Sea Of Blue",
    subdomain: null,
    domain: "aseaofblue.com",
    wpSiteId: 13,
    cycloneCommunities: {
      [cycloneCommunities.A_SEA_OF_BLUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "A Sea Of Blue",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.A_SEA_OF_BLUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 24,
      },
    },
  },
  1: {
    name: "Athletics Nation",
    displayName: "Athletics Nation",
    subdomain: null,
    domain: "athleticsnation.com",
    wpSiteId: 14,
    cycloneCommunities: {
      [cycloneCommunities.ATHLETICS_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Athletics Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ATHLETICS_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 27,
      },
    },
  },
  196: {
    name: "Arizona Desert Swarm",
    displayName: "Arizona Desert Swarm",
    subdomain: null,
    domain: "azdesertswarm.com",
    wpSiteId: 15,
    cycloneCommunities: {
      [cycloneCommunities.ARIZONA_DESERT_SWARM_CYCLONE_COMMUNITY_ID]: {
        displayName: "Arizona Desert Swarm",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ARIZONA_DESERT_SWARM_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 123,
      },
    },
  },
  12: {
    name: "AZ Snake Pit",
    displayName: "AZ Snake Pit",
    subdomain: null,
    domain: "azsnakepit.com",
    wpSiteId: 16,
    cycloneCommunities: {
      [cycloneCommunities.AZ_SNAKE_PIT_CYCLONE_COMMUNITY_ID]: {
        displayName: "AZ Snake Pit",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.AZ_SNAKE_PIT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 32,
      },
    },
  },
  232: {
    name: "Backing The Pack",
    displayName: "Backing The Pack",
    subdomain: null,
    domain: "backingthepack.com",
    wpSiteId: 17,
    cycloneCommunities: {
      [cycloneCommunities.BACKING_THE_PACK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Backing The Pack",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BACKING_THE_PACK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 139,
      },
    },
  },
  105: {
    name: "Bad Left Hook",
    displayName: "Bad Left Hook",
    subdomain: null,
    domain: "badlefthook.com",
    wpSiteId: 18,
    cycloneCommunities: {
      [cycloneCommunities.BAD_LEFT_HOOK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bad Left Hook",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BAD_LEFT_HOOK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 88,
      },
    },
  },
  81: {
    name: "Baltimore Beatdown",
    displayName: "Baltimore Beatdown",
    subdomain: null,
    domain: "baltimorebeatdown.com",
    wpSiteId: 19,
    cycloneCommunities: {
      [cycloneCommunities.BALTIMORE_BEATDOWN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Baltimore Beatdown",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BALTIMORE_BEATDOWN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 82,
      },
    },
  },
  361: {
    name: "Banners On The Parkway",
    displayName: "Banners On The Parkway",
    subdomain: null,
    domain: "bannersontheparkway.com",
    wpSiteId: 20,
    cycloneCommunities: {
      [cycloneCommunities.BANNERS_ON_THE_PARKWAY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Banners On The Parkway",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BANNERS_ON_THE_PARKWAY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 163,
      },
    },
  },
  337: {
    name: "Barca Blaugranes",
    displayName: "Barca Blaugranes",
    subdomain: null,
    domain: "barcablaugranes.com",
    wpSiteId: 21,
    cycloneCommunities: {
      [cycloneCommunities.BARCA_BLAUGRANES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Barca Blaugranes",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BARCA_BLAUGRANES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 20,
      },
    },
  },
  26: {
    name: "Battery Power",
    displayName: "Battery Power",
    subdomain: null,
    domain: "batterypower.com",
    wpSiteId: 22,
    cycloneCommunities: {
      [cycloneCommunities.BATTERY_POWER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Battery Power",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BATTERY_POWER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 7,
      },
    },
  },
  74: {
    name: "Battle Red Blog",
    displayName: "Battle Red Blog",
    subdomain: null,
    domain: "battleredblog.com",
    wpSiteId: 23,
    cycloneCommunities: {
      [cycloneCommunities.BATTLE_RED_BLOG_CYCLONE_COMMUNITY_ID]: {
        displayName: "Battle Red Blog",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BATTLE_RED_BLOG_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 77,
      },
    },
  },
  398: {
    name: "Bavarian Football Works",
    displayName: "Bavarian Football Works",
    subdomain: null,
    domain: "bavarianfootballworks.com",
    wpSiteId: 24,
    cycloneCommunities: {
      [cycloneCommunities.BAVARIAN_FOOTBALL_WORKS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bavarian Football Works",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BAVARIAN_FOOTBALL_WORKS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 174,
      },
    },
  },
  242: {
    name: "BC Interruption",
    displayName: "BC Interruption",
    subdomain: null,
    domain: "bcinterruption.com",
    wpSiteId: 25,
    cycloneCommunities: {
      [cycloneCommunities.BC_INTERRUPTION_CYCLONE_COMMUNITY_ID]: {
        displayName: "BC Interruption",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BC_INTERRUPTION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 143,
      },
    },
  },
  64: {
    name: "Behind the Steel Curtain",
    displayName: "Behind the Steel Curtain",
    subdomain: null,
    domain: "behindthesteelcurtain.com",
    wpSiteId: 26,
    cycloneCommunities: {
      [cycloneCommunities.BEHIND_THE_STEEL_CURTAIN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Behind the Steel Curtain",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BEHIND_THE_STEEL_CURTAIN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 25,
      },
    },
  },
  76: {
    name: "Big Blue View",
    displayName: "Big Blue View",
    subdomain: null,
    domain: "bigblueview.com",
    wpSiteId: 27,
    cycloneCommunities: {
      [cycloneCommunities.BIG_BLUE_VIEW_CYCLONE_COMMUNITY_ID]: {
        displayName: "Big Blue View",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BIG_BLUE_VIEW_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 19,
      },
    },
  },
  54: {
    name: "Big Cat Country",
    displayName: "Big Cat Country",
    subdomain: null,
    domain: "bigcatcountry.com",
    wpSiteId: 28,
    cycloneCommunities: {
      [cycloneCommunities.BIG_CAT_COUNTRY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Big Cat Country",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BIG_CAT_COUNTRY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 63,
      },
    },
  },
  194: {
    name: "Bitter and Blue",
    displayName: "Bitter and Blue",
    subdomain: "bitterandblue",
    domain: "bitterandblue.sbnation.com",
    wpSiteId: 29,
    cycloneCommunities: {
      [cycloneCommunities.BITTER_AND_BLUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bitter and Blue",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BITTER_AND_BLUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 122,
      },
    },
  },
  749: {
    name: "Black & Gold Banneret",
    displayName: "Black & Gold Banneret",
    subdomain: null,
    domain: "blackandgoldbanneret.com",
    wpSiteId: 30,
    cycloneCommunities: {
      [cycloneCommunities.BLACK_AND_GOLD_BANNERET_CYCLONE_COMMUNITY_ID]: {
        displayName: "Black & Gold Banneret",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLACK_AND_GOLD_BANNERET_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 185,
      },
    },
  },
  134: {
    name: "Black Heart Gold Pants",
    displayName: "Black Heart Gold Pants",
    subdomain: null,
    domain: "blackheartgoldpants.com",
    wpSiteId: 31,
    cycloneCommunities: {
      [cycloneCommunities.BLACK_HEART_GOLD_PANTS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Black Heart Gold Pants",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLACK_HEART_GOLD_PANTS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 95,
      },
    },
  },
  111: {
    name: "Black Shoe Diaries",
    displayName: "Black Shoe Diaries",
    subdomain: null,
    domain: "blackshoediaries.com",
    wpSiteId: 32,
    cycloneCommunities: {
      [cycloneCommunities.BLACK_SHOE_DIARIES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Black Shoe Diaries",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLACK_SHOE_DIARIES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 90,
      },
    },
  },
  374: {
    name: "Black & White & Read All Over",
    displayName: "Black & White & Read All Over",
    subdomain: null,
    domain: "blackwhitereadallover.com",
    wpSiteId: 33,
    cycloneCommunities: {
      [cycloneCommunities.BLACK_AND_WHITE_AND_READ_ALL_OVER_CYCLONE_COMMUNITY_ID]:
        {
          displayName: "Black & White & Read All Over",
          isPrimaryCommunity: true,
          cycloneCommunityId:
            cycloneCommunities.BLACK_AND_WHITE_AND_READ_ALL_OVER_CYCLONE_COMMUNITY_ID,
          cycloneCommunityCacheId: 168,
        },
    },
  },
  37: {
    name: "Blazer's Edge",
    displayName: "Blazer's Edge",
    subdomain: null,
    domain: "blazersedge.com",
    wpSiteId: 34,
    cycloneCommunities: {
      [cycloneCommunities.BLAZERS_EDGE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blazer's Edge",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLAZERS_EDGE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 54,
      },
    },
  },
  8: {
    name: "Bleed Cubbie Blue",
    displayName: "Bleed Cubbie Blue",
    subdomain: null,
    domain: "bleedcubbieblue.com",
    wpSiteId: 35,
    cycloneCommunities: {
      [cycloneCommunities.BLEED_CUBBIE_BLUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bleed Cubbie Blue",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLEED_CUBBIE_BLUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 30,
      },
    },
  },
  68: {
    name: "Bleeding Green Nation",
    displayName: "Bleeding Green Nation",
    subdomain: null,
    domain: "bleedinggreennation.com",
    wpSiteId: 36,
    cycloneCommunities: {
      [cycloneCommunities.BLEEDING_GREEN_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bleeding Green Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLEEDING_GREEN_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 16,
      },
    },
  },
  23: {
    name: "Bless You Boys",
    displayName: "Bless You Boys",
    subdomain: null,
    domain: "blessyouboys.com",
    wpSiteId: 37,
    cycloneCommunities: {
      [cycloneCommunities.BLESS_YOU_BOYS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bless You Boys",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLESS_YOU_BOYS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 41,
      },
    },
  },
  205: {
    name: "Blogger So Dear",
    displayName: "Blogger So Dear",
    subdomain: null,
    domain: "bloggersodear.com",
    wpSiteId: 38,
    cycloneCommunities: {
      [cycloneCommunities.BLOGGER_SO_DEAR_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blogger So Dear",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLOGGER_SO_DEAR_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 126,
      },
    },
  },
  57: {
    name: "Blogging The Boys",
    displayName: "Blogging The Boys",
    subdomain: null,
    domain: "bloggingtheboys.com",
    wpSiteId: 39,
    cycloneCommunities: {
      [cycloneCommunities.BLOGGING_THE_BOYS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blogging The Boys",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLOGGING_THE_BOYS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 21,
      },
    },
  },
  217: {
    name: "Blogging the Bracket",
    displayName: "Blogging the Bracket",
    subdomain: null,
    domain: "bloggingthebracket.com",
    wpSiteId: 40,
    cycloneCommunities: {
      [cycloneCommunities.BLOGGING_THE_BRACKET_CYCLONE_COMMUNITY_ID]: {
        displayName: "Blogging the Bracket",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLOGGING_THE_BRACKET_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 131,
      },
    },
  },
  28: {
    name: "Bluebird Banter",
    displayName: "Bluebird Banter",
    subdomain: null,
    domain: "bluebirdbanter.com",
    wpSiteId: 41,
    cycloneCommunities: {
      [cycloneCommunities.BLUEBIRD_BANTER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bluebird Banter",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BLUEBIRD_BANTER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 45,
      },
    },
  },
  167: {
    name: "Bolts From The Blue",
    displayName: "Bolts From The Blue",
    subdomain: null,
    domain: "boltsfromtheblue.com",
    wpSiteId: 42,
    cycloneCommunities: {
      [cycloneCommunities.BOLTS_FROM_THE_BLUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bolts From The Blue",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BOLTS_FROM_THE_BLUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 112,
      },
    },
  },
  13: {
    name: "Brew Crew Ball",
    displayName: "Brew Crew Ball",
    subdomain: null,
    domain: "brewcrewball.com",
    wpSiteId: 43,
    cycloneCommunities: {
      [cycloneCommunities.BREW_CREW_BALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Brew Crew Ball",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BREW_CREW_BALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 33,
      },
    },
  },
  42: {
    name: "Brew Hoop",
    displayName: "Brew Hoop",
    subdomain: null,
    domain: "brewhoop.com",
    wpSiteId: 44,
    cycloneCommunities: {
      [cycloneCommunities.BREW_HOOP_CYCLONE_COMMUNITY_ID]: {
        displayName: "Brew Hoop",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.BREW_HOOP_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 57,
      },
    },
  },
  49: {
    name: "Bright Side Of The Sun",
    displayName: "Bright Side Of The Sun",
    subdomain: null,
    domain: "brightsideofthesun.com",
    wpSiteId: 45,
    cycloneCommunities: {
      [cycloneCommunities.BRIGHT_SIDE_OF_THE_SUN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bright Side Of The Sun",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BRIGHT_SIDE_OF_THE_SUN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 60,
      },
    },
  },
  127: {
    name: "Bring On The Cats",
    displayName: "Bring On The Cats",
    subdomain: null,
    domain: "bringonthecats.com",
    wpSiteId: 46,
    cycloneCommunities: {
      [cycloneCommunities.BRING_ON_THE_CATS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bring On The Cats",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BRING_ON_THE_CATS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 94,
      },
    },
  },
  342: {
    name: "BT Powerhouse",
    displayName: "BT Powerhouse",
    subdomain: null,
    domain: "btpowerhouse.com",
    wpSiteId: 47,
    cycloneCommunities: {
      [cycloneCommunities.BT_POWERHOUSE_CYCLONE_COMMUNITY_ID]: {
        displayName: "BT Powerhouse",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BT_POWERHOUSE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 159,
      },
    },
  },
  207: {
    name: "Bucky's 5th Quarter",
    displayName: "Bucky's 5th Quarter",
    subdomain: null,
    domain: "buckys5thquarter.com",
    wpSiteId: 48,
    cycloneCommunities: {
      [cycloneCommunities.BUCKYS_5TH_QUARTER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bucky's 5th Quarter",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BUCKYS_5TH_QUARTER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 127,
      },
    },
  },
  17: {
    name: "Bucs Dugout",
    displayName: "Bucs Dugout",
    subdomain: null,
    domain: "bucsdugout.com",
    wpSiteId: 49,
    cycloneCommunities: {
      [cycloneCommunities.BUCS_DUGOUT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bucs Dugout",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.BUCS_DUGOUT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 37,
      },
    },
  },
  78: {
    name: "Bucs Nation",
    displayName: "Bucs Nation",
    subdomain: null,
    domain: "bucsnation.com",
    wpSiteId: 50,
    cycloneCommunities: {
      [cycloneCommunities.BUCS_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bucs Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.BUCS_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 80,
      },
    },
  },
  70: {
    name: "Buffalo Rumblings",
    displayName: "Buffalo Rumblings",
    subdomain: null,
    domain: "buffalorumblings.com",
    wpSiteId: 51,
    cycloneCommunities: {
      [cycloneCommunities.BUFFALO_RUMBLINGS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Buffalo Rumblings",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BUFFALO_RUMBLINGS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 73,
      },
    },
  },
  48: {
    name: "Bullets Forever",
    displayName: "Bullets Forever",
    subdomain: null,
    domain: "bulletsforever.com",
    wpSiteId: 52,
    cycloneCommunities: {
      [cycloneCommunities.BULLETS_FOREVER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bullets Forever",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BULLETS_FOREVER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 59,
      },
    },
  },
  52: {
    name: "Burnt Orange Nation",
    displayName: "Burnt Orange Nation",
    subdomain: null,
    domain: "burntorangenation.com",
    wpSiteId: 53,
    cycloneCommunities: {
      [cycloneCommunities.BURNT_ORANGE_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Burnt Orange Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BURNT_ORANGE_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 62,
      },
    },
  },
  244: {
    name: "Cageside Seats",
    displayName: "Cageside Seats",
    subdomain: null,
    domain: "cagesideseats.com",
    wpSiteId: 54,
    cycloneCommunities: {
      [cycloneCommunities.CAGESIDE_SEATS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Cageside Seats",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CAGESIDE_SEATS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 13,
      },
    },
  },
  14: {
    name: "Camden Chat",
    displayName: "Camden Chat",
    subdomain: null,
    domain: "camdenchat.com",
    wpSiteId: 55,
    cycloneCommunities: {
      [cycloneCommunities.CAMDEN_CHAT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Camden Chat",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.CAMDEN_CHAT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 34,
      },
    },
  },
  79: {
    name: "Canal Street Chronicles",
    displayName: "Canal Street Chronicles",
    subdomain: null,
    domain: "canalstreetchronicles.com",
    wpSiteId: 56,
    cycloneCommunities: {
      [cycloneCommunities.CANAL_STREET_CHRONICLES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Canal Street Chronicles",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CANAL_STREET_CHRONICLES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 81,
      },
    },
  },
  200: {
    name: "Canes Country",
    displayName: "Canes Country",
    subdomain: null,
    domain: "canescountry.com",
    wpSiteId: 57,
    cycloneCommunities: {
      [cycloneCommunities.CANES_COUNTRY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Canes Country",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CANES_COUNTRY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 124,
      },
    },
  },
  51: {
    name: "Canis Hoopus",
    displayName: "Canis Hoopus",
    subdomain: null,
    domain: "canishoopus.com",
    wpSiteId: 58,
    cycloneCommunities: {
      [cycloneCommunities.CANIS_HOOPUS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Canis Hoopus",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CANIS_HOOPUS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 61,
      },
    },
  },
  144: {
    name: "Card Chronicle",
    displayName: "Card Chronicle",
    subdomain: null,
    domain: "cardchronicle.com",
    wpSiteId: 59,
    cycloneCommunities: {
      [cycloneCommunities.CARD_CHRONICLE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Card Chronicle",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CARD_CHRONICLE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 101,
      },
    },
  },
  328: {
    name: "Cartilage Free Captain",
    displayName: "Cartilage Free Captain",
    subdomain: "cartilagefreecaptain",
    domain: "cartilagefreecaptain.sbnation.com",
    wpSiteId: 60,
    cycloneCommunities: {
      [cycloneCommunities.CARTILAGE_FREE_CAPTAIN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Cartilage Free Captain",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CARTILAGE_FREE_CAPTAIN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 157,
      },
    },
  },
  218: {
    name: "Casual Hoya",
    displayName: "Casual Hoya",
    subdomain: null,
    domain: "casualhoya.com",
    wpSiteId: 61,
    cycloneCommunities: {
      [cycloneCommunities.CASUAL_HOYA_CYCLONE_COMMUNITY_ID]: {
        displayName: "Casual Hoya",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.CASUAL_HOYA_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 132,
      },
    },
  },
  80: {
    name: "Cat Scratch Reader",
    displayName: "Cat Scratch Reader",
    subdomain: null,
    domain: "catscratchreader.com",
    wpSiteId: 62,
    cycloneCommunities: {
      [cycloneCommunities.CAT_SCRATCH_READER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Cat Scratch Reader",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CAT_SCRATCH_READER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 8,
      },
    },
  },
  175: {
    name: "CelticsBlog",
    displayName: "CelticsBlog",
    subdomain: null,
    domain: "celticsblog.com",
    wpSiteId: 63,
    cycloneCommunities: {
      [cycloneCommunities.CELTICSBLOG_CYCLONE_COMMUNITY_ID]: {
        displayName: "CelticsBlog",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.CELTICSBLOG_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 117,
      },
    },
  },
  376: {
    name: "Chiesa Di Totti",
    displayName: "Chiesa Di Totti",
    subdomain: null,
    domain: "chiesaditotti.com",
    wpSiteId: 64,
    cycloneCommunities: {
      [cycloneCommunities.CHIESA_DI_TOTTI_CYCLONE_COMMUNITY_ID]: {
        displayName: "Chiesa Di Totti",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CHIESA_DI_TOTTI_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 170,
      },
    },
  },
  66: {
    name: "Cincy Jungle",
    displayName: "Cincy Jungle",
    subdomain: null,
    domain: "cincyjungle.com",
    wpSiteId: 65,
    cycloneCommunities: {
      [cycloneCommunities.CINCY_JUNGLE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Cincy Jungle",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CINCY_JUNGLE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 71,
      },
    },
  },
  346: {
    name: "Coming Home Newcastle",
    displayName: "Coming Home Newcastle",
    subdomain: "cominghomenewcastle",
    domain: "cominghomenewcastle.sbnation.com",
    wpSiteId: 66,
    cycloneCommunities: {
      [cycloneCommunities.COMING_HOME_NEWCASTLE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Coming Home Newcastle",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.COMING_HOME_NEWCASTLE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 160,
      },
    },
  },
  141: {
    name: "Corn Nation",
    displayName: "Corn Nation",
    subdomain: null,
    domain: "cornnation.com",
    wpSiteId: 67,
    cycloneCommunities: {
      [cycloneCommunities.CORN_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Corn Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.CORN_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 98,
      },
    },
  },
  157: {
    name: "CougCenter",
    displayName: "CougCenter",
    subdomain: null,
    domain: "cougcenter.com",
    wpSiteId: 68,
    cycloneCommunities: {
      [cycloneCommunities.COUGCENTER_CYCLONE_COMMUNITY_ID]: {
        displayName: "CougCenter",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.COUGCENTER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 107,
      },
    },
  },
  15: {
    name: "Covering the Corner",
    displayName: "Covering the Corner",
    subdomain: null,
    domain: "coveringthecorner.com",
    wpSiteId: 69,
    cycloneCommunities: {
      [cycloneCommunities.COVERING_THE_CORNER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Covering the Corner",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.COVERING_THE_CORNER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 35,
      },
    },
  },
  21: {
    name: "The Crawfish Boxes",
    displayName: "The Crawfish Boxes",
    subdomain: null,
    domain: "crawfishboxes.com",
    wpSiteId: 70,
    cycloneCommunities: {
      [cycloneCommunities.THE_CRAWFISH_BOXES_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Crawfish Boxes",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_CRAWFISH_BOXES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 40,
      },
    },
  },
  215: {
    name: "The Crimson Quarry",
    displayName: "The Crimson Quarry",
    subdomain: null,
    domain: "crimsonquarry.com",
    wpSiteId: 71,
    cycloneCommunities: {
      [cycloneCommunities.THE_CRIMSON_QUARRY_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Crimson Quarry",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_CRIMSON_QUARRY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 130,
      },
    },
  },
  65: {
    name: "Daily Norseman",
    displayName: "Daily Norseman",
    subdomain: null,
    domain: "dailynorseman.com",
    wpSiteId: 72,
    cycloneCommunities: {
      [cycloneCommunities.DAILY_NORSEMAN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Daily Norseman",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.DAILY_NORSEMAN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 70,
      },
    },
  },
  62: {
    name: "Dawgs By Nature",
    displayName: "Dawgs By Nature",
    subdomain: null,
    domain: "dawgsbynature.com",
    wpSiteId: 73,
    cycloneCommunities: {
      [cycloneCommunities.DAWGS_BY_NATURE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Dawgs By Nature",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.DAWGS_BY_NATURE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 69,
      },
    },
  },
  114: {
    name: "Dawg Sports",
    displayName: "Dawg Sports",
    subdomain: null,
    domain: "dawgsports.com",
    wpSiteId: 74,
    cycloneCommunities: {
      [cycloneCommunities.DAWG_SPORTS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Dawg Sports",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.DAWG_SPORTS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 92,
      },
    },
  },
  222: {
    name: "Detroit Bad Boys",
    displayName: "Detroit Bad Boys",
    subdomain: null,
    domain: "detroitbadboys.com",
    wpSiteId: 75,
    cycloneCommunities: {
      [cycloneCommunities.DETROIT_BAD_BOYS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Detroit Bad Boys",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.DETROIT_BAD_BOYS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 133,
      },
    },
  },
  31: {
    name: "DRaysBay",
    displayName: "DRaysBay",
    subdomain: null,
    domain: "draysbay.com",
    wpSiteId: 76,
    cycloneCommunities: {
      [cycloneCommunities.DRAYSBAY_CYCLONE_COMMUNITY_ID]: {
        displayName: "DRaysBay",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.DRAYSBAY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 48,
      },
    },
  },
  417: {
    name: "Duke Basketball Report",
    displayName: "Duke Basketball Report",
    subdomain: null,
    domain: "dukebasketballreport.com",
    wpSiteId: 77,
    cycloneCommunities: {
      [cycloneCommunities.DUKE_BASKETBALL_REPORT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Duke Basketball Report",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.DUKE_BASKETBALL_REPORT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 180,
      },
    },
  },
  158: {
    name: "Fear The Sword",
    displayName: "Fear The Sword",
    subdomain: null,
    domain: "fearthesword.com",
    wpSiteId: 78,
    cycloneCommunities: {
      [cycloneCommunities.FEAR_THE_SWORD_CYCLONE_COMMUNITY_ID]: {
        displayName: "Fear The Sword",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.FEAR_THE_SWORD_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 108,
      },
    },
  },
  621: {
    name: "Fear The Wall",
    displayName: "Fear The Wall",
    subdomain: null,
    domain: "fearthewall.com",
    wpSiteId: 79,
    cycloneCommunities: {
      [cycloneCommunities.FEAR_THE_WALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Fear The Wall",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.FEAR_THE_WALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 183,
      },
    },
  },
  27: {
    name: "Federal Baseball",
    displayName: "Federal Baseball",
    subdomain: null,
    domain: "federalbaseball.com",
    wpSiteId: 80,
    cycloneCommunities: {
      [cycloneCommunities.FEDERAL_BASEBALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Federal Baseball",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.FEDERAL_BASEBALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 44,
      },
    },
  },
  60: {
    name: "Field Gulls",
    displayName: "Field Gulls",
    subdomain: null,
    domain: "fieldgulls.com",
    wpSiteId: 81,
    cycloneCommunities: {
      [cycloneCommunities.FIELD_GULLS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Field Gulls",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.FIELD_GULLS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 67,
      },
    },
  },
  354: {
    name: "FMF State Of Mind",
    displayName: "FMF State Of Mind",
    subdomain: null,
    domain: "fmfstateofmind.com",
    wpSiteId: 82,
    cycloneCommunities: {
      [cycloneCommunities.FMF_STATE_OF_MIND_CYCLONE_COMMUNITY_ID]: {
        displayName: "FMF State Of Mind",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.FMF_STATE_OF_MIND_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 162,
      },
    },
  },
  635: {
    name: "Fosse Posse",
    displayName: "Fosse Posse",
    subdomain: "fosseposse",
    domain: "fosseposse.sbnation.com",
    wpSiteId: 83,
    cycloneCommunities: {
      [cycloneCommunities.FOSSE_POSSE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Fosse Posse",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.FOSSE_POSSE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 184,
      },
    },
  },
  366: {
    name: "Frogs O' War",
    displayName: "Frogs O' War",
    subdomain: null,
    domain: "frogsowar.com",
    wpSiteId: 84,
    cycloneCommunities: {
      [cycloneCommunities.FROGS_O_WAR_CYCLONE_COMMUNITY_ID]: {
        displayName: "Frogs O' War",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.FROGS_O_WAR_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 165,
      },
    },
  },
  234: {
    name: "From The Rumble Seat",
    displayName: "From The Rumble Seat",
    subdomain: null,
    domain: "fromtherumbleseat.com",
    wpSiteId: 85,
    cycloneCommunities: {
      [cycloneCommunities.FROM_THE_RUMBLE_SEAT_CYCLONE_COMMUNITY_ID]: {
        displayName: "From The Rumble Seat",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.FROM_THE_RUMBLE_SEAT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 140,
      },
    },
  },
  166: {
    name: "Gang Green Nation",
    displayName: "Gang Green Nation",
    subdomain: null,
    domain: "ganggreennation.com",
    wpSiteId: 86,
    cycloneCommunities: {
      [cycloneCommunities.GANG_GREEN_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Gang Green Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.GANG_GREEN_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 9,
      },
    },
  },
  33: {
    name: "Gaslamp Ball",
    displayName: "Gaslamp Ball",
    subdomain: null,
    domain: "gaslampball.com",
    wpSiteId: 87,
    cycloneCommunities: {
      [cycloneCommunities.GASLAMP_BALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Gaslamp Ball",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.GASLAMP_BALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 50,
      },
    },
  },
  226: {
    name: "Gobbler Country",
    displayName: "Gobbler Country",
    subdomain: null,
    domain: "gobblercountry.com",
    wpSiteId: 88,
    cycloneCommunities: {
      [cycloneCommunities.GOBBLER_COUNTRY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Gobbler Country",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.GOBBLER_COUNTRY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 135,
      },
    },
  },
  36: {
    name: "Golden State Of Mind",
    displayName: "Golden State Of Mind",
    subdomain: null,
    domain: "goldenstateofmind.com",
    wpSiteId: 89,
    cycloneCommunities: {
      [cycloneCommunities.GOLDEN_STATE_OF_MIND_CYCLONE_COMMUNITY_ID]: {
        displayName: "Golden State Of Mind",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.GOLDEN_STATE_OF_MIND_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 53,
      },
    },
  },
  402: {
    name: "Good Bull Hunting",
    displayName: "Good Bull Hunting",
    subdomain: null,
    domain: "goodbullhunting.com",
    wpSiteId: 90,
    cycloneCommunities: {
      [cycloneCommunities.GOOD_BULL_HUNTING_CYCLONE_COMMUNITY_ID]: {
        displayName: "Good Bull Hunting",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.GOOD_BULL_HUNTING_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 176,
      },
    },
  },
  213: {
    name: "Hammer and Rails",
    displayName: "Hammer and Rails",
    subdomain: null,
    domain: "hammerandrails.com",
    wpSiteId: 91,
    cycloneCommunities: {
      [cycloneCommunities.HAMMER_AND_RAILS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Hammer and Rails",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.HAMMER_AND_RAILS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 129,
      },
    },
  },
  61: {
    name: "Hogs Haven",
    displayName: "Hogs Haven",
    subdomain: null,
    domain: "hogshaven.com",
    wpSiteId: 92,
    cycloneCommunities: {
      [cycloneCommunities.HOGS_HAVEN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Hogs Haven",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.HOGS_HAVEN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 68,
      },
    },
  },
  318: {
    name: "Hustle Belt",
    displayName: "Hustle Belt",
    subdomain: null,
    domain: "hustlebelt.com",
    wpSiteId: 93,
    cycloneCommunities: {
      [cycloneCommunities.HUSTLE_BELT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Hustle Belt",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.HUSTLE_BELT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 15,
      },
    },
  },
  246: {
    name: "Inside NU",
    displayName: "Inside NU",
    subdomain: null,
    domain: "insidenu.com",
    wpSiteId: 94,
    cycloneCommunities: {
      [cycloneCommunities.INSIDE_NU_CYCLONE_COMMUNITY_ID]: {
        displayName: "Inside NU",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.INSIDE_NU_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 144,
      },
    },
  },
  562: {
    name: "Into the Calderon",
    displayName: "Into the Calderon",
    subdomain: null,
    domain: "intothecalderon.com",
    wpSiteId: 95,
    cycloneCommunities: {
      [cycloneCommunities.INTO_THE_CALDERON_CYCLONE_COMMUNITY_ID]: {
        displayName: "Into the Calderon",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.INTO_THE_CALDERON_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 182,
      },
    },
  },
  400: {
    name: "Land-Grant Holy Land",
    displayName: "Land-Grant Holy Land",
    subdomain: null,
    domain: "landgrantholyland.com",
    wpSiteId: 96,
    cycloneCommunities: {
      [cycloneCommunities.LAND_GRANT_HOLY_LAND_CYCLONE_COMMUNITY_ID]: {
        displayName: "Land-Grant Holy Land",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.LAND_GRANT_HOLY_LAND_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 175,
      },
    },
  },
  171: {
    name: "Liberty Ballers",
    displayName: "Liberty Ballers",
    subdomain: null,
    domain: "libertyballers.com",
    wpSiteId: 97,
    cycloneCommunities: {
      [cycloneCommunities.LIBERTY_BALLERS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Liberty Ballers",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.LIBERTY_BALLERS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 115,
      },
    },
  },
  170: {
    name: "Lighthouse Hockey",
    displayName: "Lighthouse Hockey",
    subdomain: null,
    domain: "lighthousehockey.com",
    wpSiteId: 98,
    cycloneCommunities: {
      [cycloneCommunities.LIGHTHOUSE_HOCKEY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Lighthouse Hockey",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.LIGHTHOUSE_HOCKEY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 114,
      },
    },
  },
  373: {
    name: "The Liverpool Offside",
    displayName: "The Liverpool Offside",
    subdomain: "liverpooloffside",
    domain: "liverpooloffside.sbnation.com",
    wpSiteId: 99,
    cycloneCommunities: {
      [cycloneCommunities.THE_LIVERPOOL_OFFSIDE_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Liverpool Offside",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_LIVERPOOL_OFFSIDE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 167,
      },
    },
  },
  10: {
    name: "Lone Star Ball",
    displayName: "Lone Star Ball",
    subdomain: null,
    domain: "lonestarball.com",
    wpSiteId: 100,
    cycloneCommunities: {
      [cycloneCommunities.LONE_STAR_BALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Lone Star Ball",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.LONE_STAR_BALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 31,
      },
    },
  },
  29: {
    name: "Lookout Landing",
    displayName: "Lookout Landing",
    subdomain: null,
    domain: "lookoutlanding.com",
    wpSiteId: 101,
    cycloneCommunities: {
      [cycloneCommunities.LOOKOUT_LANDING_CYCLONE_COMMUNITY_ID]: {
        displayName: "Lookout Landing",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.LOOKOUT_LANDING_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 46,
      },
    },
  },
  84: {
    name: "Maize n Brew",
    displayName: "Maize n Brew",
    subdomain: null,
    domain: "maizenbrew.com",
    wpSiteId: 102,
    cycloneCommunities: {
      [cycloneCommunities.MAIZE_N_BREW_CYCLONE_COMMUNITY_ID]: {
        displayName: "Maize n Brew",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MAIZE_N_BREW_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 85,
      },
    },
  },
  364: {
    name: "Managing Madrid",
    displayName: "Managing Madrid",
    subdomain: null,
    domain: "managingmadrid.com",
    wpSiteId: 103,
    cycloneCommunities: {
      [cycloneCommunities.MANAGING_MADRID_CYCLONE_COMMUNITY_ID]: {
        displayName: "Managing Madrid",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MANAGING_MADRID_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 164,
      },
    },
  },
  38: {
    name: "Mavs Moneyball",
    displayName: "Mavs Moneyball",
    subdomain: null,
    domain: "mavsmoneyball.com",
    wpSiteId: 104,
    cycloneCommunities: {
      [cycloneCommunities.MAVS_MONEYBALL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Mavs Moneyball",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MAVS_MONEYBALL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 55,
      },
    },
  },
  4: {
    name: "McCovey Chronicles",
    displayName: "McCovey Chronicles",
    subdomain: null,
    domain: "mccoveychronicles.com",
    wpSiteId: 105,
    cycloneCommunities: {
      [cycloneCommunities.MCCOVEY_CHRONICLES_CYCLONE_COMMUNITY_ID]: {
        displayName: "McCovey Chronicles",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MCCOVEY_CHRONICLES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 28,
      },
    },
  },
  231: {
    name: "Mid-Major Madness",
    displayName: "Mid-Major Madness",
    subdomain: null,
    domain: "midmajormadness.com",
    wpSiteId: 106,
    cycloneCommunities: {
      [cycloneCommunities.MID_MAJOR_MADNESS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Mid-Major Madness",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MID_MAJOR_MADNESS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 138,
      },
    },
  },
  125: {
    name: "Mile High Hockey",
    displayName: "Mile High Hockey",
    subdomain: null,
    domain: "milehighhockey.com",
    wpSiteId: 107,
    cycloneCommunities: {
      [cycloneCommunities.MILE_HIGH_HOCKEY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Mile High Hockey",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MILE_HIGH_HOCKEY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 93,
      },
    },
  },
  55: {
    name: "Mile High Report",
    displayName: "Mile High Report",
    subdomain: null,
    domain: "milehighreport.com",
    wpSiteId: 108,
    cycloneCommunities: {
      [cycloneCommunities.MILE_HIGH_REPORT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Mile High Report",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MILE_HIGH_REPORT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 64,
      },
    },
  },
  395: {
    name: "MMA Fighting",
    displayName: "MMA Fighting",
    subdomain: null,
    domain: "mmafighting.com",
    wpSiteId: 109,
    cycloneCommunities: {
      [cycloneCommunities.MMA_FIGHTING_CYCLONE_COMMUNITY_ID]: {
        displayName: "MMA Fighting",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MMA_FIGHTING_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 12,
      },
    },
  },
  258: {
    name: "MMA Mania",
    displayName: "MMA Mania",
    subdomain: null,
    domain: "mmamania.com",
    wpSiteId: 110,
    cycloneCommunities: {
      [cycloneCommunities.MMAMANIA_CYCLONE_COMMUNITY_ID]: {
        displayName: "MMA Mania",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.MMAMANIA_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 14,
      },
    },
  },
  72: {
    name: "Music City Miracles",
    displayName: "Music City Miracles",
    subdomain: null,
    domain: "musiccitymiracles.com",
    wpSiteId: 111,
    cycloneCommunities: {
      [cycloneCommunities.MUSIC_CITY_MIRACLES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Music City Miracles",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MUSIC_CITY_MIRACLES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 75,
      },
    },
  },
  237: {
    name: "Mountain West Connection",
    displayName: "Mountain West Connection",
    subdomain: null,
    domain: "mwcconnection.com",
    wpSiteId: 112,
    cycloneCommunities: {
      [cycloneCommunities.MOUNTAIN_WEST_CONNECTION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Mountain West Connection",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.MOUNTAIN_WEST_CONNECTION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 141,
      },
    },
  },
  276: {
    name: "NetsDaily",
    displayName: "NetsDaily",
    subdomain: null,
    domain: "netsdaily.com",
    wpSiteId: 113,
    cycloneCommunities: {
      [cycloneCommunities.NETSDAILY_CYCLONE_COMMUNITY_ID]: {
        displayName: "NetsDaily",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.NETSDAILY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 150,
      },
    },
  },
  375: {
    name: "Never Manage Alone",
    displayName: "Never Manage Alone",
    subdomain: null,
    domain: "nevermanagealone.com",
    wpSiteId: 114,
    cycloneCommunities: {
      [cycloneCommunities.NEVER_MANAGE_ALONE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Never Manage Alone",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.NEVER_MANAGE_ALONE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 169,
      },
    },
  },
  67: {
    name: "Niners Nation",
    displayName: "Niners Nation",
    subdomain: null,
    domain: "ninersnation.com",
    wpSiteId: 115,
    cycloneCommunities: {
      [cycloneCommunities.NINERS_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Niners Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.NINERS_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 22,
      },
    },
  },
  168: {
    name: "Troy Nunes Is An Absolute Magician",
    displayName: "Troy Nunes Is An Absolute Magician",
    subdomain: null,
    domain: "nunesmagician.com",
    wpSiteId: 116,
    cycloneCommunities: {
      [cycloneCommunities.TROY_NUNES_IS_AN_ABSOLUTE_MAGICIAN_CYCLONE_COMMUNITY_ID]:
        {
          displayName: "Troy Nunes Is An Absolute Magician",
          isPrimaryCommunity: true,
          cycloneCommunityId:
            cycloneCommunities.TROY_NUNES_IS_AN_ABSOLUTE_MAGICIAN_CYCLONE_COMMUNITY_ID,
          cycloneCommunityCacheId: 113,
        },
    },
  },
  160: {
    name: "Off Tackle Empire",
    displayName: "Off Tackle Empire",
    subdomain: null,
    domain: "offtackleempire.com",
    wpSiteId: 117,
    cycloneCommunities: {
      [cycloneCommunities.OFF_TACKLE_EMPIRE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Off Tackle Empire",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.OFF_TACKLE_EMPIRE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 110,
      },
    },
  },
  368: {
    name: "One Foot Down",
    displayName: "One Foot Down",
    subdomain: null,
    domain: "onefootdown.com",
    wpSiteId: 118,
    cycloneCommunities: {
      [cycloneCommunities.ONE_FOOT_DOWN_CYCLONE_COMMUNITY_ID]: {
        displayName: "One Foot Down",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ONE_FOOT_DOWN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 166,
      },
    },
  },
  275: {
    name: "On the Banks",
    displayName: "On the Banks",
    subdomain: null,
    domain: "onthebanks.com",
    wpSiteId: 119,
    cycloneCommunities: {
      [cycloneCommunities.ON_THE_BANKS_CYCLONE_COMMUNITY_ID]: {
        displayName: "On the Banks",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ON_THE_BANKS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 149,
      },
    },
  },
  34: {
    name: "Over the Monster",
    displayName: "Over the Monster",
    subdomain: null,
    domain: "overthemonster.com",
    wpSiteId: 120,
    cycloneCommunities: {
      [cycloneCommunities.OVER_THE_MONSTER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Over the Monster",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.OVER_THE_MONSTER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 51,
      },
    },
  },
  82: {
    name: "Pats Pulpit",
    displayName: "Pats Pulpit",
    subdomain: null,
    domain: "patspulpit.com",
    wpSiteId: 121,
    cycloneCommunities: {
      [cycloneCommunities.PATS_PULPIT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Pats Pulpit",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.PATS_PULPIT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 83,
      },
    },
  },
  161: {
    name: "Peachtree Hoops",
    displayName: "Peachtree Hoops",
    subdomain: null,
    domain: "peachtreehoops.com",
    wpSiteId: 122,
    cycloneCommunities: {
      [cycloneCommunities.PEACHTREE_HOOPS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Peachtree Hoops",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.PEACHTREE_HOOPS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 111,
      },
    },
  },
  138: {
    name: "PensBurgh",
    displayName: "PensBurgh",
    subdomain: null,
    domain: "pensburgh.com",
    wpSiteId: 123,
    cycloneCommunities: {
      [cycloneCommunities.PENSBURGH_CYCLONE_COMMUNITY_ID]: {
        displayName: "PensBurgh",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.PENSBURGH_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 97,
      },
    },
  },
  11: {
    name: "Pinstripe Alley",
    displayName: "Pinstripe Alley",
    subdomain: null,
    domain: "pinstripealley.com",
    wpSiteId: 124,
    cycloneCommunities: {
      [cycloneCommunities.PINSTRIPE_ALLEY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Pinstripe Alley",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.PINSTRIPE_ALLEY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 23,
      },
    },
  },
  46: {
    name: "Posting and Toasting",
    displayName: "Posting and Toasting",
    subdomain: null,
    domain: "postingandtoasting.com",
    wpSiteId: 125,
    cycloneCommunities: {
      [cycloneCommunities.POSTING_AND_TOASTING_CYCLONE_COMMUNITY_ID]: {
        displayName: "Posting and Toasting",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.POSTING_AND_TOASTING_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 58,
      },
    },
  },
  39: {
    name: "Pounding The Rock",
    displayName: "Pounding The Rock",
    subdomain: null,
    domain: "poundingtherock.com",
    wpSiteId: 126,
    cycloneCommunities: {
      [cycloneCommunities.POUNDING_THE_ROCK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Pounding The Rock",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.POUNDING_THE_ROCK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 56,
      },
    },
  },
  63: {
    name: "Pride Of Detroit",
    displayName: "Pride Of Detroit",
    subdomain: null,
    domain: "prideofdetroit.com",
    wpSiteId: 127,
    cycloneCommunities: {
      [cycloneCommunities.PRIDE_OF_DETROIT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Pride Of Detroit",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.PRIDE_OF_DETROIT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 17,
      },
    },
  },
  19: {
    name: "Purple Row",
    displayName: "Purple Row",
    subdomain: null,
    domain: "purplerow.com",
    wpSiteId: 128,
    cycloneCommunities: {
      [cycloneCommunities.PURPLE_ROW_CYCLONE_COMMUNITY_ID]: {
        displayName: "Purple Row",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.PURPLE_ROW_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 38,
      },
    },
  },
  156: {
    name: "The Ralphie Report",
    displayName: "The Ralphie Report",
    subdomain: null,
    domain: "ralphiereport.com",
    wpSiteId: 129,
    cycloneCommunities: {
      [cycloneCommunities.THE_RALPHIE_REPORT_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Ralphie Report",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_RALPHIE_REPORT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 106,
      },
    },
  },
  230: {
    name: "Raptors HQ",
    displayName: "Raptors HQ",
    subdomain: null,
    domain: "raptorshq.com",
    wpSiteId: 130,
    cycloneCommunities: {
      [cycloneCommunities.RAPTORS_HQ_CYCLONE_COMMUNITY_ID]: {
        displayName: "Raptors HQ",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.RAPTORS_HQ_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 137,
      },
    },
  },
  211: {
    name: "Red Cup Rebellion",
    displayName: "Red Cup Rebellion",
    subdomain: null,
    domain: "redcuprebellion.com",
    wpSiteId: 131,
    cycloneCommunities: {
      [cycloneCommunities.RED_CUP_REBELLION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Red Cup Rebellion",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.RED_CUP_REBELLION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 128,
      },
    },
  },
  25: {
    name: "Red Reporter",
    displayName: "Red Reporter",
    subdomain: null,
    domain: "redreporter.com",
    wpSiteId: 132,
    cycloneCommunities: {
      [cycloneCommunities.RED_REPORTER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Red Reporter",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.RED_REPORTER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 43,
      },
    },
  },
  83: {
    name: "Revenge of the Birds",
    displayName: "Revenge of the Birds",
    subdomain: null,
    domain: "revengeofthebirds.com",
    wpSiteId: 133,
    cycloneCommunities: {
      [cycloneCommunities.REVENGE_OF_THE_BIRDS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Revenge of the Birds",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.REVENGE_OF_THE_BIRDS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 84,
      },
    },
  },
  148: {
    name: "Rock Chalk Talk",
    displayName: "Rock Chalk Talk",
    subdomain: null,
    domain: "rockchalktalk.com",
    wpSiteId: 134,
    cycloneCommunities: {
      [cycloneCommunities.ROCK_CHALK_TALK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Rock Chalk Talk",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROCK_CHALK_TALK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 103,
      },
    },
  },
  137: {
    name: "Rock M Nation",
    displayName: "Rock M Nation",
    subdomain: null,
    domain: "rockmnation.com",
    wpSiteId: 135,
    cycloneCommunities: {
      [cycloneCommunities.ROCK_M_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Rock M Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROCK_M_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 96,
      },
    },
  },
  143: {
    name: "Rocky Top Talk",
    displayName: "Rocky Top Talk",
    subdomain: null,
    domain: "rockytoptalk.com",
    wpSiteId: 136,
    cycloneCommunities: {
      [cycloneCommunities.ROCKY_TOP_TALK_CYCLONE_COMMUNITY_ID]: {
        displayName: "Rocky Top Talk",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROCKY_TOP_TALK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 100,
      },
    },
  },
  350: {
    name: "Roker Report",
    displayName: "Roker Report",
    subdomain: "rokerreport",
    domain: "rokerreport.sbnation.com",
    wpSiteId: 137,
    cycloneCommunities: {
      [cycloneCommunities.ROKER_REPORT_CYCLONE_COMMUNITY_ID]: {
        displayName: "Roker Report",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROKER_REPORT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 161,
      },
    },
  },
  107: {
    name: "Roll 'Bama Roll",
    displayName: "Roll 'Bama Roll",
    subdomain: null,
    domain: "rollbamaroll.com",
    wpSiteId: 138,
    cycloneCommunities: {
      [cycloneCommunities.ROLL_BAMA_ROLL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Roll 'Bama Roll",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROLL_BAMA_ROLL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 89,
      },
    },
  },
  289: {
    name: "Royal Blue Mersey",
    displayName: "Royal Blue Mersey",
    subdomain: "royalbluemersey",
    domain: "royalbluemersey.sbnation.com",
    wpSiteId: 139,
    cycloneCommunities: {
      [cycloneCommunities.ROYAL_BLUE_MERSEY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Royal Blue Mersey",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROYAL_BLUE_MERSEY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 154,
      },
    },
  },
  16: {
    name: "Royals Review",
    displayName: "Royals Review",
    subdomain: null,
    domain: "royalsreview.com",
    wpSiteId: 140,
    cycloneCommunities: {
      [cycloneCommunities.ROYALS_REVIEW_CYCLONE_COMMUNITY_ID]: {
        displayName: "Royals Review",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ROYALS_REVIEW_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 36,
      },
    },
  },
  341: {
    name: "Rumble In The Garden",
    displayName: "Rumble In The Garden",
    subdomain: null,
    domain: "rumbleinthegarden.com",
    wpSiteId: 141,
    cycloneCommunities: {
      [cycloneCommunities.RUMBLE_IN_THE_GARDEN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Rumble In The Garden",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.RUMBLE_IN_THE_GARDEN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 158,
      },
    },
  },
  255: {
    name: "Shakin The Southland",
    displayName: "Shakin The Southland",
    subdomain: null,
    domain: "shakinthesouthland.com",
    wpSiteId: 142,
    cycloneCommunities: {
      [cycloneCommunities.SHAKIN_THE_SOUTHLAND_CYCLONE_COMMUNITY_ID]: {
        displayName: "Shakin The Southland",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.SHAKIN_THE_SOUTHLAND_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 146,
      },
    },
  },
  71: {
    name: "Silver And Black Pride",
    displayName: "Silver And Black Pride",
    subdomain: null,
    domain: "silverandblackpride.com",
    wpSiteId: 143,
    cycloneCommunities: {
      [cycloneCommunities.SILVER_AND_BLACK_PRIDE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Silver And Black Pride",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.SILVER_AND_BLACK_PRIDE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 74,
      },
    },
  },
  227: {
    name: "Silver Screen and Roll",
    displayName: "Silver Screen and Roll",
    subdomain: null,
    domain: "silverscreenandroll.com",
    wpSiteId: 144,
    cycloneCommunities: {
      [cycloneCommunities.SILVER_SCREEN_AND_ROLL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Silver Screen and Roll",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.SILVER_SCREEN_AND_ROLL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 136,
      },
    },
  },
  113: {
    name: "SLC Dunk",
    displayName: "SLC Dunk",
    subdomain: null,
    domain: "slcdunk.com",
    wpSiteId: 145,
    cycloneCommunities: {
      [cycloneCommunities.SLC_DUNK_CYCLONE_COMMUNITY_ID]: {
        displayName: "SLC Dunk",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.SLC_DUNK_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 91,
      },
    },
  },
  187: {
    name: "The Slipper Still Fits",
    displayName: "The Slipper Still Fits",
    subdomain: null,
    domain: "slipperstillfits.com",
    wpSiteId: 146,
    cycloneCommunities: {
      [cycloneCommunities.THE_SLIPPER_STILL_FITS_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Slipper Still Fits",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_SLIPPER_STILL_FITS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 119,
      },
    },
  },
  193: {
    name: "The Smoking Musket",
    displayName: "The Smoking Musket",
    subdomain: null,
    domain: "smokingmusket.com",
    wpSiteId: 147,
    cycloneCommunities: {
      [cycloneCommunities.THE_SMOKING_MUSKET_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Smoking Musket",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_SMOKING_MUSKET_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 121,
      },
    },
  },
  20: {
    name: "South Side Sox",
    displayName: "South Side Sox",
    subdomain: null,
    domain: "southsidesox.com",
    wpSiteId: 148,
    cycloneCommunities: {
      [cycloneCommunities.SOUTH_SIDE_SOX_CYCLONE_COMMUNITY_ID]: {
        displayName: "South Side Sox",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.SOUTH_SIDE_SOX_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 39,
      },
    },
  },
  56: {
    name: "Stampede Blue",
    displayName: "Stampede Blue",
    subdomain: null,
    domain: "stampedeblue.com",
    wpSiteId: 149,
    cycloneCommunities: {
      [cycloneCommunities.STAMPEDE_BLUE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Stampede Blue",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.STAMPEDE_BLUE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 65,
      },
    },
  },
  188: {
    name: "Stanley Cup of Chowder",
    displayName: "Stanley Cup of Chowder",
    subdomain: null,
    domain: "stanleycupofchowder.com",
    wpSiteId: 150,
    cycloneCommunities: {
      [cycloneCommunities.STANLEY_CUP_OF_CHOWDER_CYCLONE_COMMUNITY_ID]: {
        displayName: "Stanley Cup of Chowder",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.STANLEY_CUP_OF_CHOWDER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 120,
      },
    },
  },
  404: {
    name: "Stars and Stripes FC",
    displayName: "Stars and Stripes FC",
    subdomain: null,
    domain: "starsandstripesfc.com",
    wpSiteId: 151,
    cycloneCommunities: {
      [cycloneCommunities.STARS_AND_STRIPES_FC_CYCLONE_COMMUNITY_ID]: {
        displayName: "Stars and Stripes FC",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.STARS_AND_STRIPES_FC_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 178,
      },
    },
  },
  204: {
    name: "State of The U",
    displayName: "State of The U",
    subdomain: null,
    domain: "stateoftheu.com",
    wpSiteId: 152,
    cycloneCommunities: {
      [cycloneCommunities.STATE_OF_THE_U_CYCLONE_COMMUNITY_ID]: {
        displayName: "State of The U",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.STATE_OF_THE_U_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 125,
      },
    },
  },
  326: {
    name: "Streaking The Lawn",
    displayName: "Streaking The Lawn",
    subdomain: null,
    domain: "streakingthelawn.com",
    wpSiteId: 153,
    cycloneCommunities: {
      [cycloneCommunities.STREAKING_THE_LAWN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Streaking The Lawn",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.STREAKING_THE_LAWN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 156,
      },
    },
  },
  248: {
    name: "Swish Appeal",
    displayName: "Swish Appeal",
    subdomain: null,
    domain: "swishappeal.com",
    wpSiteId: 154,
    cycloneCommunities: {
      [cycloneCommunities.SWISH_APPEAL_CYCLONE_COMMUNITY_ID]: {
        displayName: "Swish Appeal",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.SWISH_APPEAL_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 145,
      },
    },
  },
  142: {
    name: "Tar Heel Blog",
    displayName: "Tar Heel Blog",
    subdomain: null,
    domain: "tarheelblog.com",
    wpSiteId: 155,
    cycloneCommunities: {
      [cycloneCommunities.TAR_HEEL_BLOG_CYCLONE_COMMUNITY_ID]: {
        displayName: "Tar Heel Blog",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.TAR_HEEL_BLOG_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 99,
      },
    },
  },
  151: {
    name: "Testudo Times",
    displayName: "Testudo Times",
    subdomain: null,
    domain: "testudotimes.com",
    wpSiteId: 156,
    cycloneCommunities: {
      [cycloneCommunities.TESTUDO_TIMES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Testudo Times",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.TESTUDO_TIMES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 104,
      },
    },
  },
  277: {
    name: "The Busby Babe",
    displayName: "The Busby Babe",
    subdomain: "thebusbybabe",
    domain: "thebusbybabe.sbnation.com",
    wpSiteId: 157,
    cycloneCommunities: {
      [cycloneCommunities.THE_BUSBY_BABE_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Busby Babe",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_BUSBY_BABE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 151,
      },
    },
  },
  407: {
    name: "The Champaign Room",
    displayName: "The Champaign Room",
    subdomain: null,
    domain: "thechampaignroom.com",
    wpSiteId: 158,
    cycloneCommunities: {
      [cycloneCommunities.THE_CHAMPAIGN_ROOM_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Champaign Room",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_CHAMPAIGN_ROOM_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 179,
      },
    },
  },
  153: {
    name: "The Daily Gopher",
    displayName: "The Daily Gopher",
    subdomain: null,
    domain: "thedailygopher.com",
    wpSiteId: 159,
    cycloneCommunities: {
      [cycloneCommunities.THE_DAILY_GOPHER_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Daily Gopher",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_DAILY_GOPHER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 105,
      },
    },
  },
  172: {
    name: "The Dream Shake",
    displayName: "The Dream Shake",
    subdomain: null,
    domain: "thedreamshake.com",
    wpSiteId: 160,
    cycloneCommunities: {
      [cycloneCommunities.THE_DREAM_SHAKE_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Dream Shake",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_DREAM_SHAKE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 116,
      },
    },
  },
  73: {
    name: "The Falcoholic",
    displayName: "The Falcoholic",
    subdomain: null,
    domain: "thefalcoholic.com",
    wpSiteId: 161,
    cycloneCommunities: {
      [cycloneCommunities.THE_FALCOHOLIC_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Falcoholic",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_FALCOHOLIC_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 76,
      },
    },
  },
  24: {
    name: "The Good Phight",
    displayName: "The Good Phight",
    subdomain: null,
    domain: "thegoodphight.com",
    wpSiteId: 162,
    cycloneCommunities: {
      [cycloneCommunities.THE_GOOD_PHIGHT_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Good Phight",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_GOOD_PHIGHT_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 42,
      },
    },
  },
  224: {
    name: "The Only Colors",
    displayName: "The Only Colors",
    subdomain: null,
    domain: "theonlycolors.com",
    wpSiteId: 163,
    cycloneCommunities: {
      [cycloneCommunities.THE_ONLY_COLORS_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Only Colors",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_ONLY_COLORS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 134,
      },
    },
  },
  69: {
    name: "The Phinsider",
    displayName: "The Phinsider",
    subdomain: null,
    domain: "thephinsider.com",
    wpSiteId: 164,
    cycloneCommunities: {
      [cycloneCommunities.THE_PHINSIDER_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Phinsider",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_PHINSIDER_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 72,
      },
    },
  },
  286: {
    name: "The Short Fuse",
    displayName: "The Short Fuse",
    subdomain: "theshortfuse",
    domain: "theshortfuse.sbnation.com",
    wpSiteId: 165,
    cycloneCommunities: {
      [cycloneCommunities.THE_SHORT_FUSE_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Short Fuse",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_SHORT_FUSE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 153,
      },
    },
  },
  392: {
    name: "The Tilehurst End",
    displayName: "The Tilehurst End",
    subdomain: "thetilehurstend",
    domain: "thetilehurstend.sbnation.com",
    wpSiteId: 166,
    cycloneCommunities: {
      [cycloneCommunities.THE_TILEHURST_END_CYCLONE_COMMUNITY_ID]: {
        displayName: "The Tilehurst End",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_TILEHURST_END_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 173,
      },
    },
  },
  270: {
    name: "The UConn Blog",
    displayName: "The UConn Blog",
    subdomain: null,
    domain: "theuconnblog.com",
    wpSiteId: 167,
    cycloneCommunities: {
      [cycloneCommunities.THE_UCONN_BLOG_CYCLONE_COMMUNITY_ID]: {
        displayName: "The UConn Blog",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.THE_UCONN_BLOG_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 148,
      },
    },
  },
  99: {
    name: "Tomahawk Nation",
    displayName: "Tomahawk Nation",
    subdomain: null,
    domain: "tomahawknation.com",
    wpSiteId: 168,
    cycloneCommunities: {
      [cycloneCommunities.TOMAHAWK_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Tomahawk Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.TOMAHAWK_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 86,
      },
    },
  },
  32: {
    name: "True Blue LA",
    displayName: "True Blue LA",
    subdomain: null,
    domain: "truebluela.com",
    wpSiteId: 169,
    cycloneCommunities: {
      [cycloneCommunities.TRUE_BLUE_LA_CYCLONE_COMMUNITY_ID]: {
        displayName: "True Blue LA",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.TRUE_BLUE_LA_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 49,
      },
    },
  },
  75: {
    name: "Turf Show Times",
    displayName: "Turf Show Times",
    subdomain: null,
    domain: "turfshowtimes.com",
    wpSiteId: 170,
    cycloneCommunities: {
      [cycloneCommunities.TURF_SHOW_TIMES_CYCLONE_COMMUNITY_ID]: {
        displayName: "Turf Show Times",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.TURF_SHOW_TIMES_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 78,
      },
    },
  },
  30: {
    name: "Twinkie Town",
    displayName: "Twinkie Town",
    subdomain: null,
    domain: "twinkietown.com",
    wpSiteId: 171,
    cycloneCommunities: {
      [cycloneCommunities.TWINKIE_TOWN_CYCLONE_COMMUNITY_ID]: {
        displayName: "Twinkie Town",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.TWINKIE_TOWN_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 47,
      },
    },
  },
  448: {
    name: "Underdog Dynasty",
    displayName: "Underdog Dynasty",
    subdomain: null,
    domain: "underdogdynasty.com",
    wpSiteId: 172,
    cycloneCommunities: {
      [cycloneCommunities.UNDERDOG_DYNASTY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Underdog Dynasty",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.UNDERDOG_DYNASTY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 181,
      },
    },
  },
  159: {
    name: "UW Dawg Pound",
    displayName: "UW Dawg Pound",
    subdomain: null,
    domain: "uwdawgpound.com",
    wpSiteId: 173,
    cycloneCommunities: {
      [cycloneCommunities.UW_DAWG_POUND_CYCLONE_COMMUNITY_ID]: {
        displayName: "UW Dawg Pound",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.UW_DAWG_POUND_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 109,
      },
    },
  },
  238: {
    name: "Vanquish The Foe",
    displayName: "Vanquish The Foe",
    subdomain: null,
    domain: "vanquishthefoe.com",
    wpSiteId: 174,
    cycloneCommunities: {
      [cycloneCommunities.VANQUISH_THE_FOE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Vanquish The Foe",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.VANQUISH_THE_FOE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 142,
      },
    },
  },
  403: {
    name: "Viola Nation",
    displayName: "Viola Nation",
    subdomain: null,
    domain: "violanation.com",
    wpSiteId: 175,
    cycloneCommunities: {
      [cycloneCommunities.VIOLA_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Viola Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.VIOLA_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 177,
      },
    },
  },
  5: {
    name: "Viva El Birdos",
    displayName: "Viva El Birdos",
    subdomain: null,
    domain: "vivaelbirdos.com",
    wpSiteId: 176,
    cycloneCommunities: {
      [cycloneCommunities.VIVA_EL_BIRDOS_CYCLONE_COMMUNITY_ID]: {
        displayName: "Viva El Birdos",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.VIVA_EL_BIRDOS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 29,
      },
    },
  },
  285: {
    name: "VU Hoops",
    displayName: "VU Hoops",
    subdomain: null,
    domain: "vuhoops.com",
    wpSiteId: 177,
    cycloneCommunities: {
      [cycloneCommunities.VU_HOOPS_CYCLONE_COMMUNITY_ID]: {
        displayName: "VU Hoops",
        isPrimaryCommunity: true,
        cycloneCommunityId: cycloneCommunities.VU_HOOPS_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 26,
      },
    },
  },
  319: {
    name: "We Ain't Got No History",
    displayName: "We Ain't Got No History",
    subdomain: "weaintgotnohistory",
    domain: "weaintgotnohistory.sbnation.com",
    wpSiteId: 178,
    cycloneCommunities: {
      [cycloneCommunities.WE_AINT_GOT_NO_HISTORY_CYCLONE_COMMUNITY_ID]: {
        displayName: "We Ain't Got No History",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.WE_AINT_GOT_NO_HISTORY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 155,
      },
    },
  },
  377: {
    name: "Wide Right & Natty Lite",
    displayName: "Wide Right & Natty Lite",
    subdomain: null,
    domain: "widerightnattylite.com",
    wpSiteId: 179,
    cycloneCommunities: {
      [cycloneCommunities.WIDE_RIGHT_AND_NATTY_LITE_CYCLONE_COMMUNITY_ID]: {
        displayName: "Wide Right & Natty Lite",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.WIDE_RIGHT_AND_NATTY_LITE_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 171,
      },
    },
  },
  58: {
    name: "Windy City Gridiron",
    displayName: "Windy City Gridiron",
    subdomain: null,
    domain: "windycitygridiron.com",
    wpSiteId: 180,
    cycloneCommunities: {
      [cycloneCommunities.WINDY_CITY_GRIDIRON_CYCLONE_COMMUNITY_ID]: {
        displayName: "Windy City Gridiron",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.WINDY_CITY_GRIDIRON_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 66,
      },
    },
  },
  85: {
    name: "Alligator Army",
    displayName: "Alligator Army",
    subdomain: null,
    domain: "alligatorarmy.com",
    wpSiteId: 901,
    isFeedOnlyCommunity: true,
    cycloneCommunities: {
      [cycloneCommunities.ALLIGATOR_ARMY_CYCLONE_COMMUNITY_ID]: {
        displayName: "Alligator Army",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.ALLIGATOR_ARMY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 187,
      },
    },
  },
  152: {
    name: "House of Sparky",
    displayName: "House of Sparky",
    subdomain: null,
    domain: "houseofsparky.com",
    wpSiteId: 902,
    isFeedOnlyCommunity: true,
    cycloneCommunities: {
      [cycloneCommunities.HOUSE_OF_SPARKY_CYCLONE_COMMUNITY_ID]: {
        displayName: "House of Sparky",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.HOUSE_OF_SPARKY_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 190,
      },
    },
  },
  106: {
    name: "Bruins Nation",
    displayName: "Bruins Nation",
    subdomain: null,
    domain: "bruinsnation.com",
    wpSiteId: 903,
    isFeedOnlyCommunity: true,
    cycloneCommunities: {
      [cycloneCommunities.BRUINS_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Bruins Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.BRUINS_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 188,
      },
    },
  },
  47: {
    name: "Clips Nation",
    displayName: "Clips Nation",
    subdomain: null,
    domain: "clipsnation.com",
    wpSiteId: 904,
    isFeedOnlyCommunity: true,
    cycloneCommunities: {
      [cycloneCommunities.CLIPS_NATION_CYCLONE_COMMUNITY_ID]: {
        displayName: "Clips Nation",
        isPrimaryCommunity: true,
        cycloneCommunityId:
          cycloneCommunities.CLIPS_NATION_CYCLONE_COMMUNITY_ID,
        cycloneCommunityCacheId: 189,
      },
    },
  },
};

/**
 * Reverse index from cyclone community ID to the extra Honeypot feed-query
 * context needed for mobile-feed expansion.
 */
export const SB_NATION_CYCLONE_COMMUNITY_FEED_QUERY_MAP: Record<
  string,
  CycloneCommunityFeedQueryConfig
> = Object.entries(SB_NATION_COMMUNITIES).reduce<
  Record<string, CycloneCommunityFeedQueryConfig>
>((accumulator, [chorusCommunityId, community]) => {
  for (const cycloneCommunity of Object.values(
    community.cycloneCommunities ?? {},
  )) {
    const isFanpostOnlyNestedCommunity =
      !cycloneCommunity.isPrimaryCommunity &&
      (cycloneCommunity.feedCategoryFilter?.length ?? 0) === 0 &&
      (cycloneCommunity.feedSuperCategoryFilter?.length ?? 0) === 0;
    // Fanpost-only path-prefix communities should not expand onto Chorus WP content.
    if (isFanpostOnlyNestedCommunity) {
      continue;
    }
    accumulator[cycloneCommunity.cycloneCommunityId] = {
      chorusCommunityId: Number(chorusCommunityId),
      ...(cycloneCommunity.feedCategoryFilter
        ? {
            feedCategoryFilter: [
              ...new Set(cycloneCommunity.feedCategoryFilter),
            ].sort(),
          }
        : {}),
      ...(cycloneCommunity.feedSuperCategoryFilter
        ? {
            feedSuperCategoryFilter: [
              ...new Set(cycloneCommunity.feedSuperCategoryFilter),
            ].sort(),
          }
        : {}),
    };
  }

  return accumulator;
}, {});
